/**
 * Smooth in-page anchor scrolling.
 *
 * Handles same-page hash links (e.g. "Join the Challenge" -> #join-challenge),
 * offsets for a sticky header, and re-settles the scroll position while
 * late-rendering content — lazy images, Klaviyo app embeds — changes the
 * page layout underneath the visitor.
 */

const HEADER_SELECTOR = '.section-header';
const OPT_OUT_SELECTOR = '[data-no-smooth-scroll], .skip-to-content-link';
const SCROLL_GAP = 24;
const SETTLE_DURATION = 3000;
const SETTLE_TOLERANCE = 2;

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const getScrollBehavior = () => (prefersReducedMotion() ? 'auto' : 'smooth');

const getStickyHeaderOffset = () => {
  const header = document.querySelector(HEADER_SELECTOR);
  if (!header) return 0;

  return window.getComputedStyle(header).position === 'sticky' ? header.offsetHeight : 0;
};

const getScrollPosition = (element) => {
  const elementTop = element.getBoundingClientRect().top + window.scrollY;
  const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);

  return Math.max(0, Math.min(elementTop - getStickyHeaderOffset() - SCROLL_GAP, maxScroll));
};

/**
 * Keeps the viewport pinned to the anchor while content above or below it
 * finishes rendering, and bails out as soon as the visitor scrolls themselves.
 */
const settleScrollPosition = (element, initialPosition) => {
  if (typeof ResizeObserver === 'undefined') return;

  let lastPosition = initialPosition;

  const stop = () => {
    observer.disconnect();
    window.clearTimeout(timeoutId);
    window.removeEventListener('wheel', stop);
    window.removeEventListener('touchstart', stop);
    window.removeEventListener('keydown', stop);
  };

  const observer = new ResizeObserver(() => {
    const nextPosition = getScrollPosition(element);
    if (Math.abs(nextPosition - lastPosition) <= SETTLE_TOLERANCE) return;

    lastPosition = nextPosition;
    window.scrollTo({ top: nextPosition, behavior: getScrollBehavior() });
  });

  const timeoutId = window.setTimeout(stop, SETTLE_DURATION);

  window.addEventListener('wheel', stop, { passive: true, once: true });
  window.addEventListener('touchstart', stop, { passive: true, once: true });
  window.addEventListener('keydown', stop, { once: true });

  observer.observe(document.body);
};

const focusAnchor = (anchor) => {
  if (!anchor.hasAttribute('tabindex')) anchor.setAttribute('tabindex', '-1');
  anchor.focus({ preventScroll: true });
};

const scrollToAnchor = (anchor) => {
  const position = getScrollPosition(anchor);

  window.scrollTo({ top: position, behavior: getScrollBehavior() });
  focusAnchor(anchor);
  settleScrollPosition(anchor, position);
};

const getAnchorFromHash = (hash) => {
  if (!hash || hash.length < 2) return null;

  const id = hash.slice(1);

  try {
    return document.getElementById(decodeURIComponent(id));
  } catch {
    return document.getElementById(id);
  }
};

const isSamePageLink = (link) =>
  link.host === window.location.host && link.pathname === window.location.pathname;

const handleClick = (event) => {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const link = event.target.closest('a[href*="#"]');
  if (!link || link.target === '_blank' || link.matches(OPT_OUT_SELECTOR)) return;
  if (!isSamePageLink(link)) return;

  const anchor = getAnchorFromHash(link.hash);
  if (!anchor) return;

  event.preventDefault();
  scrollToAnchor(anchor);

  if (window.location.hash !== link.hash) {
    window.history.pushState(null, '', link.hash);
  }
};

const handleInitialHash = () => {
  const anchor = getAnchorFromHash(window.location.hash);
  if (!anchor) return;

  scrollToAnchor(anchor);
};

document.addEventListener('DOMContentLoaded', () => {
  document.addEventListener('click', handleClick);
  handleInitialHash();
});
